import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import InputField from '#component/form/input';
import SelectProject from '#component/form/multiple-select-project';
import DateTimeField from '#component/form/date-time-field';
import { nextStep, setAfterSubmitAction } from '#action/shipment/form';
import { fetchShipmentProjects } from '#action/shipment/dashboard';
import { fetchProjectAlerts } from '#action/project/alerts';
import { fetchProjectProducts } from '#action/project/products';
import { fetchProjectPackages } from '#action/project/packages';
import { fetchProjectCustomFields } from '#action/project/custom-fields';
import { fetchSelectProjects, clearSelectProjects } from '#action/project/project-select';
import PreloaderComponent from '#component/preloader';
import { isEnabledModuleById } from '#component/project/form/helper';
import CustomFields from '#component/shipment/form/shipment/custom-fields/index';
import { formatCustomField } from '#component/shipment/form/helper';
import propTypes from './prop-types';
import {
  StyledPaper,
  ButtonCnt,
  StyledButton,
  FormWrapper,
  PreloaderCtn,
  LabelField,
  BeginDateField,
  EndDateField,
  ProjectField
} from './style';
import { isProjectExpired, requestStatuses as statuses } from '#helper';

const Form = props => {
  const {
    editable,
    project,
    submitting,
    initialValues: iv,
    shipmentId,
    dispatch,
    SaveButton = () => null,
    CancelButton = () => null,
    resetProducts,
    formStatus,
    isCopiedShipment,
    shipment,
    onFormSubmit
  } = props;
  const methods = useFormContext();
  const { control, handleSubmit, watch, setValue, formState } = methods || {};
  const { isValid } = formState || {};
  const [init, setInit] = useState(false);
  const [hasProject, setHasProject] = useState(project ?? false);
  const { loading } = statuses;
  const formLoading = formStatus === loading;
  const SHIPMENT_MODULE_ID = 1;
  const customFieldsRef = useRef();
  const selectedProject = watch?.('project');
  const customFields = useSelector(s => s.projectCustomFields, shallowEqual);
  const { projects, status } = useSelector(s => s.projectSelect, shallowEqual);

  const filteredProjects = useMemo(
    () => projects.filter(p => !isProjectExpired(p) && !p.deleted),
    [projects]
  );

  const getShipmentAlertIds = useCallback(() => {
    if ((shipmentId || isCopiedShipment) && shipment?.alerts?.length) {
      const uniqueIds = [...new Set(shipment.alerts.map(a => a.id))];

      return uniqueIds.join(',');
    }

    return null;
  }, [shipment?.alerts, shipmentId, isCopiedShipment]);

  useEffect(() => {
    if (selectedProject) {
      const fetchData = async () => {
        await dispatch(fetchShipmentProjects(selectedProject));
        await dispatch(fetchProjectAlerts(selectedProject, false, null, getShipmentAlertIds()));
        await dispatch(fetchProjectProducts(selectedProject, true));
        await dispatch(fetchProjectPackages(selectedProject, true));
        setInit(true);
      };
      fetchData();
    }
  }, [selectedProject, dispatch, getShipmentAlertIds]);

  // to prevent stale ref from a previous flow from firing and overwriting form values.
  useEffect(() => {
    customFieldsRef.current = null;
  }, [shipmentId, isCopiedShipment]);

  useEffect(() => {
    // (i.e. this is NOT an edit or copy flow where values come from initialValues).
    if (customFieldsRef.current && !shipmentId && !isCopiedShipment && !['idle', 'loading'].includes(customFields.status)) {
      customFieldsRef.current = null;
      const customFieldsData = formatCustomField(customFields.customFields);
      setValue?.('customFields', customFieldsData);
    } else if (customFieldsRef.current && (shipmentId || isCopiedShipment)) {
      // We are in edit/copy mode; clear the stale ref without overwriting values.
      customFieldsRef.current = null;
    }
  }, [setValue, customFields, dispatch, selectedProject, shipmentId, isCopiedShipment]);

  const handleNextClick = () => {
    const next = () => {
      dispatch(nextStep());
    };

    dispatch(setAfterSubmitAction(next));
  };

  const handleProjectChange = projectId => {
    customFieldsRef.current = projectId;
    setHasProject(true);
    dispatch(fetchProjectCustomFields(projectId));
  };

  const handleProjectsFetch = e => dispatch(fetchSelectProjects(e.target.value, true));

  const clearProjects = () => {
    dispatch(clearSelectProjects());
  };

  const fetchProjects = () => {
    dispatch(fetchSelectProjects(null, true));
  };

  if (selectedProject && !init) {
    return <PreloaderComponent />;
  }

  const isLoading = isValid && formLoading;

  return (
    <FormWrapper autoComplete="off" onSubmit={handleSubmit(onFormSubmit)} isLoading={isLoading}>
      {isLoading ? (
        <PreloaderCtn>
          <PreloaderComponent />
        </PreloaderCtn>) : null}
      <StyledPaper elevation={0} isLoading={isLoading}>
        <LabelField>
          <Controller
            name="label"
            control={control}
            render={({ field, fieldState }) => {
              const { isSubmitted: submitted } = methods.formState;
              return (
                <InputField
                  {...field}
                  meta={{ ...fieldState, isSubmitted: submitted }}
                  label="Label"
                  variant="outlined"
                  fullWidth
                  disabled={!editable}
                />
              );
            }}
          />
        </LabelField>
        <BeginDateField>
          <Controller
            name="beginDate"
            control={control}
            render={({ field, fieldState }) => {
              const { isSubmitted: submitted } = methods.formState;
              return (
                <DateTimeField
                  {...field}
                  meta={{ ...fieldState, isSubmitted: submitted }}
                  label="Start date"
                  fullWidth
                  disabled={!editable}
                />
              );
            }}
          />
        </BeginDateField>
        <EndDateField>
          <Controller
            name="endDate"
            control={control}
            render={({ field, fieldState }) => {
              const { isSubmitted: submitted } = methods.formState;
              return (
                <DateTimeField
                  {...field}
                  meta={{ ...fieldState, isSubmitted: submitted }}
                  label="Due date"
                  fullWidth
                  disabled={!editable}
                />
              );
            }}
          />
        </EndDateField>
        <ProjectField hasMobilePlaceholder={hasProject}>
          <Controller
            name="project"
            control={control}
            render={({ field, fieldState }) => {
              const { isSubmitted: submitted } = methods.formState;
              return (
                <SelectProject
                  {...field}
                  meta={{ ...fieldState, isSubmitted: submitted }}
                  hasMobilePlaceholder={hasProject}
                  label="Project"
                  placeholder="Project"
                  data={filteredProjects.map(i => ({
                    label: `${i.title}`,
                    value: i.id,
                    disabled: !isEnabledModuleById(i.modules, SHIPMENT_MODULE_ID),
                    disabledMsg: 'Enable module',
                    ...i
                  }))}
                  setData={p => {
                    field.onChange(p.value);
                    setValue?.('project', p.value, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
                    setValue?.('customFields', null);
                    resetProducts();
                    handleProjectChange(p.value);
                  }}
                  defaultData={project}
                  disabled={!isCopiedShipment && (!editable || !!iv?.project)}
                  fetchProjects={handleProjectsFetch}
                  clearProjects={clearProjects}
                  loading={['loading'].includes(status)}
                  textFieldOnFocus={fetchProjects}
                  showDateRange={true}
                />
              );
            }}
          />
        </ProjectField>
        {iv?.customFields && (
          <CustomFields
            fields={iv.customFields}
            customFields={iv.customFields}
          />
        )}
      </StyledPaper>
      <ButtonCnt isLoading={isLoading}>
        <StyledButton as={CancelButton} />
        <StyledButton
          disabled={submitting}
          type="submit"
          variant="outlined"
          onClick={handleNextClick}
        >
          Next
        </StyledButton>
        {shipmentId && editable && <StyledButton as={SaveButton} disabled={submitting} />}
      </ButtonCnt>
    </FormWrapper>
  );
};

Form.propTypes = propTypes;

export default Form;
